import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import { type NotePreviewRow, NotesPreviewDocument } from "#app/lib/data";

const PREVIEW_FLOOR = 5;

/**
 * Hover-preview data for a kanban card's notes button. Owns its own slim
 * `NotesPreview` query (not the kanban board query, not the activity view's
 * IssueNotesData) so the board read stays lean and each note carries its
 * per-user `unread` flag.
 *
 * Within the fetched window (the most-recently-updated notes — see the
 * query's `limit`), unread notes come first, then the most-recent
 * read notes up to a five-row floor. It's a preview, not an exhaustive unread
 * list: an unread note older than the window may not appear (the card's red
 * dot is driven separately by Issue.hasUnreadNote, which is exact). Mounts
 * only while one card's popover is open, so it's a single active query.
 */
export function useNotesPreview(issueId: string): {
  notes: NotePreviewRow[];
  loading: boolean;
} {
  const variables = useMemo(() => ({ issueId }), [issueId]);
  const { data, loading } = useQuery(NotesPreviewDocument, {
    variables,
    fetchPolicy: "cache-and-network",
  });

  const notes = useMemo(() => {
    const all: NotePreviewRow[] =
      data?.notes?.edges?.map((e) => e.node) ?? [];
    const time = (n: NotePreviewRow) =>
      new Date(n.lastUpdateDate ?? n.creationDate).getTime();
    const byRecency = (a: NotePreviewRow, b: NotePreviewRow) =>
      time(b) - time(a);
    const unread = all.filter((n) => n.unread).sort(byRecency);
    const read = all.filter((n) => !n.unread).sort(byRecency);
    return [...unread, ...read].slice(0, Math.max(PREVIEW_FLOOR, unread.length));
  }, [data]);

  return { notes, loading };
}
